import React from 'react'
import ReactTable, { RowInfo, SortingRule, TableProps as OriginalTableProps } from 'react-table'
import { Waypoint } from 'react-waypoint'
import Loader from '../Loader/Loader'

import 'react-table/react-table.css'
import './Table.scss'

export interface ITableProps extends Partial<OriginalTableProps<any>> {
  /**
   * Is table loading more data
   */
  isLoading?: boolean
  /**
   * Callback when user scrolls to the bottom of the table
   */
  fetchMoreData?(): void
  handleSortedChange?(newSorted: SortingRule[]): void
}

export class Table extends React.Component<ITableProps> {
  state = {
    expanded: { 0: false },
  }

  static defaultProps: ITableProps = {
    defaultSortDesc: true,
    resizable: false,
    showPageJump: false,
    showPageSizeOptions: false,
    showPagination: false,
  }

  static rankBEM(max: number) {
    const classArray = ['rt-td--rank']

    if (max < 10) {
      classArray.push('rt-td--rank-xs')
    }

    if (max >= 10 && max < 100) {
      classArray.push('rt-td--rank-sm')
    }

    return classArray.join(' ')
  }

  handleWaypoint = (position: Waypoint.CallbackArgs) => {
    const { isLoading, fetchMoreData } = this.props

    if (!isLoading && fetchMoreData && position.currentPosition === 'inside') {
      fetchMoreData()
    }
  }

  getExpanded = () => {
    return this.state.expanded
  }

  setExpanded = (e: any) => {
    this.setState({
      expanded: e,
    })
  }

  render() {
    const { data, isLoading, fetchMoreData, handleSortedChange, ...rest } = this.props

    // Sortable column headers follow the WAI-ARIA grid pattern: keep the th as a
    // columnheader, wrap its label in a <button> so the interactive control has
    // proper button semantics (instead of putting role="button" on the th, which
    // would violate aria-required-children on role="row"). aria-sort is emitted
    // below via getTheadThProps so screen readers announce the sort state.
    /*const wrapHeaderInSortButton = (originalHeader: any) => {
      const Button = (cellInfo?: any) => (
        <button type="button" className="rt-th-sort-button">
          {typeof originalHeader === 'function' ? originalHeader(cellInfo) : originalHeader}
        </button>
      )
      return Button
    }*/

    const originalColumns = (rest as any).columns as any[] | undefined
    /*const columns = originalColumns?.map((col) => {
      if (!col || col.sortable === false) return col
      return { ...col, Header: wrapHeaderInSortButton(col.Header) }
    })*/

    // react-table v6 renders the whole table as role-less <div>s, so screen
    // readers see no table structure and cannot associate headers with cells
    // (WCAG 1.3.1). Map react-table's div layers onto the ARIA table roles:
    //   rt-table  -> table
    //   rt-thead  -> rowgroup        rt-tbody    -> rowgroup
    //   rt-tr     -> row             rt-tr-group -> presentation (extra wrapper)
    //   rt-th     -> columnheader    rt-td       -> cell
    const getTableProps = () => ({ role: 'table' })
    const getTheadProps = () => ({ role: 'rowgroup' })
    const getTheadTrProps = () => ({ role: 'row' })
    const getTheadThProps = (state: any, _rowInfo: any, column: any) => {
      const base: any = { role: 'columnheader' }
      if (!column || column.sortable === false) return base
      const sortEntry = state?.sorted?.find((s: any) => s.id === column.id)
      return {
        ...base,
        'aria-sort': sortEntry ? (sortEntry.desc ? 'descending' : 'ascending') : 'none',
      }
    }
    const getTbodyProps = () => ({ role: 'rowgroup' })
    // The per-row wrapper has no place in the ARIA table model; hide it so the
    // rt-tr rows are owned directly by the rt-tbody rowgroup.
    const getTrGroupProps = () => ({ role: 'presentation' })
    const getTdProps = () => ({ role: 'cell' })

    const getClassName = (
      {
        original: {
          isCompleted,
          isSelf,
          isBeforeSelf,
          isSeparatorForAbove,
          isSeparatorForBelow,
          accentColor,
          alignRight,
        },
      } = {
        original: {
          isCompleted: false,
          isSelf: false,
          isBeforeSelf: false,
          isSeparatorForAbove: false,
          isSeparatorForBelow: false,
          accentColor: undefined,
          alignRight: false,
        },
      },
    ) => {
      const classArray = []

      if (isBeforeSelf) {
        classArray.push('rt-tr--before-self')
      }

      if (isSelf) {
        classArray.push('rt-tr--self')
      }

      if (isCompleted) {
        classArray.push('rt-tr--active')
      }

      if (isSeparatorForAbove) {
        classArray.push('rt-tr--separator-for-above')
      }

      if (isSeparatorForBelow) {
        classArray.push('rt-tr--separator-for-below')
      }

      if (accentColor) {
        classArray.push(`rt-tr--accent-${accentColor}`)
      }

      if (alignRight) {
        classArray.push('rt-tr--align-right')
      }

      return classArray.join(' ')
    }

    const getTrProps = (state: any, rowInfo: RowInfo | undefined) => ({
      role: 'row',
      className: getClassName(rowInfo),
    })

    return (
      <div className="table">
        {data && data.length ? (
          <>
            <ReactTable
              {...rest}
              //columns={columns || originalColumns}
              columns={originalColumns}
              getTableProps={getTableProps}
              getTrProps={getTrProps}
              getTheadThProps={getTheadThProps}
              getTheadProps={getTheadProps}
              getTheadTrProps={getTheadTrProps}
              getTbodyProps={getTbodyProps}
              getTrGroupProps={getTrGroupProps}
              getTdProps={getTdProps}
              data={data}
              pageSize={data.length}
              onSortedChange={handleSortedChange}
              expanded={this.getExpanded()}
              onExpandedChange={(e) => {
                this.setExpanded(e)
              }}
            />
            {fetchMoreData && !isLoading && <Waypoint onEnter={this.handleWaypoint} bottomOffset={-2500} />}
          </>
        ) : null}
        {(!data || !data.length || fetchMoreData) && isLoading && <Loader />}
      </div>
    )
  }
}
